import {isString} from 'lodash';

import periodicUpdater from './incidentViewportPeriodicUpdater';
import styleResolver, {SOURCE_RASTER} from './styleParts/styleResolver';
import trafficIncidentCommons from './trafficIncidentCommons';
import rasterIncidentTiles from './styleParts/rasterTrafficIncidentTiles';
import {vectorTrafficTilesResolver} from 'Map/tier/styleParts/vectorStyleResolver';
import initializeStyleOption from 'Map/tier/styleParts/initializeStyleOption';
import sourceNameReplacer from './sourceNameReplacer';
import {trafficIncidentsUrlTransformer} from 'Map/tier/urlTransformers/urlTransformers';
import constants from '../../core/constants';
import updateLayersVisibility from 'Map/tier/styleUpdate/updateLayersVisibility';

export const ID = 'trafficIncidentsTiles';
export const VECTOR_STYLE_SOURCE_NAME = 'vectorTrafficIncidentTiles';

function replaceOrAddTrafficModelIdToUrl(url, trafficModelId) {
    const trafficModelIdParamPattern = /([?&])t=[^&]+/;
    if (trafficModelIdParamPattern.test(url)) {
        return url.replace(trafficModelIdParamPattern, `$1t=${trafficModelId}`);
    } else if (url.indexOf('?') === -1) {
        return url + `?t=${trafficModelId}`;
    } else {
        return url + `&t=${trafficModelId}`;
    }
}

function replaceTrafficModelIdInTilesStyle(style, trafficModelId) {
    Object.keys(style.sources).forEach((sourceName) => {
        const tiles = style.sources[sourceName].tiles;
        style.sources[sourceName].tiles = tiles.map((tile) => replaceOrAddTrafficModelIdToUrl(tile, trafficModelId));
    });
}

export class TrafficIncidentTilesTier {
    constructor(options, position) {
        this.position = position;
        this.style = null;
        this.options = options;
        this.trafficModelId = constants.VIEWPORT_DEFAULT_VALUE;
        this.urlTransformer = trafficIncidentsUrlTransformer(options.key, options.incidentTiles.theme.getSource(),
            () => this.trafficModelId);
        this.visible = false;

        periodicUpdater.set(this.options.refresh, this.options.key);
    }
    getId = () => ID;
    getPosition = () => this.position;
    getStyle = () => this.style;
    setStyle = (style) => {
        // This is done, because traffic incidents tiles layers
        // and traffic flow tiles layers coming from API
        // were referencing the same source, which caused mapbox error.
        sourceNameReplacer(style, VECTOR_STYLE_SOURCE_NAME);
        replaceTrafficModelIdInTilesStyle(style, this.trafficModelId);
        this.style = style;
    };
    getOptions = () => this.options;
    isVisible = () => this.visible;
    setVisible = (visible) => {
        this.visible = visible;
    };

    _getSourceName = () => {
        if (isString(this.style)) {
            throw new Error('Cannot find a source name for a not expanded style');
        }

        return Object.keys(this.style.sources)[0];
    };

    _refresh = async (map, trafficModelId) => {
        trafficIncidentCommons.deregisterMapEventsHandlers(this.map, this._mapEventHandler);
        this.trafficModelId = trafficModelId;
        if (isString(this.style)) {
            trafficIncidentCommons.registerMapEventsHandlers(this.map, this._mapEventHandler);
            return;
        }
        replaceTrafficModelIdInTilesStyle(this.style, trafficModelId);
        if (!this.isRaster()) {
            map.__om.style.sourceCaches[this._getSourceName()].clearTiles();
        }
        updateLayersVisibility(map, this.getStyle().layers, 'none');
        updateLayersVisibility(map, this.getStyle().layers, 'visible');
        trafficIncidentCommons.registerMapEventsHandlers(this.map, this._mapEventHandler);
    };

    onBeforeAdd = () => {
        const styleResolverFactory = (styleResolverOptions) =>
            styleResolver(
                styleResolverOptions.unwrapedTheme,
                () => rasterIncidentTiles(styleResolverOptions.unwrapedTheme.style),
                () => vectorTrafficTilesResolver(styleResolverOptions)
            );

        const styleResolverOptions = {
            theme: this.options.incidentTiles.theme,
            key: this.options.key
        };

        this.style = initializeStyleOption(styleResolverOptions, styleResolverFactory);
    };

    onAdd = (map) => {
        this.map = map;
        trafficIncidentCommons.registerMapEventsHandlers(this.map, this._mapEventHandler);
        periodicUpdater.addOnModelIdRetrievedCallback(ID, async (trafficModelId) => {
            await this._refresh(map, trafficModelId);
        });
        map.once('remove', this.onRemove);
    };

    onRemove = () => {
        periodicUpdater.removeOnModelIdRetrievedCallback(ID);
        trafficIncidentCommons.deregisterMapEventsHandlers(this.map, this._mapEventHandler);
        this.map.off('remove', this.onRemove);
    };

    getUrlTransformer = () => this.urlTransformer;

    isRaster = () => this.options.incidentTiles.theme.unwrap().source === SOURCE_RASTER;

    _mapEventHandler = () => {
        periodicUpdater.refreshTrafficModelIdAndExecuteCallbacks();
    };
}

export default (options, position) => {
    return new TrafficIncidentTilesTier(options, position);
};



// WEBPACK FOOTER //
// ./map/tier/trafficIncidentTilesTier.js